import React from 'react';
import { Link } from 'react-router-dom';

const TermsOfServicePage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <section className="bg-gradient-to-br from-mimo-primary to-mimo-accent text-white py-20">
        <div className="container-custom">
          <h1 className="text-4xl md:text-5xl font-extrabold font-heading mb-4">
            Terms of Service
          </h1>
          <p className="text-xl text-white/90">
            Last Updated: February 2026
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding">
        <div className="container-custom max-w-4xl">
          <div className="prose prose-slate max-w-none">
            
            <h2 className="text-2xl font-bold font-heading text-slate-900 mt-8 mb-4">Agreement to Terms</h2>
            <p className="text-slate-600 mb-4">
              These Terms of Service ("Terms") govern your use of the mimoIT Technology Solutions website and services. By accessing or using our website at <a href="https://mimoit.com" className="text-mimo-primary hover:text-mimo-accent">mimoit.com</a> or engaging our services, you agree to be bound by these Terms. If you do not agree to these Terms, please do not use our website or services.
            </p>

            <h2 className="text-2xl font-bold font-heading text-slate-900 mt-8 mb-4">Services Overview</h2>
            <p className="text-slate-600 mb-4">
              mimoIT Technology Solutions ("mimoIT," "we," "us," or "our") provides boutique technology solutions including:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Managed IT services and infrastructure management</li>
              <li>Cybersecurity and compliance services</li>
              <li>Physical security systems (surveillance, access control)</li>
              <li>Cloud computing and SaaS management</li>
              <li>Network infrastructure and telecommunications</li>
              <li>Business continuity and disaster recovery</li>
              <li>Help desk and technical support</li>
            </ul>

            <h2 className="text-2xl font-bold font-heading text-slate-900 mt-8 mb-4">Service Agreements</h2>
            <p className="text-slate-600 mb-4">
              Specific services are governed by individual Service Level Agreements (SLAs) and Master Service Agreements (MSAs) executed between mimoIT and the client. These Terms supplement, but do not replace, any written agreements you have with us. In the event of a conflict, the written service agreement takes precedence.
            </p>

            <h2 className="text-2xl font-bold font-heading text-slate-900 mt-8 mb-4">User Responsibilities</h2>
            <p className="text-slate-600 mb-4">
              As a user of our services, you agree to:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Provide accurate and complete information</li>
              <li>Maintain the confidentiality of account credentials</li>
              <li>Notify us immediately of any unauthorized access</li>
              <li>Comply with all applicable laws and regulations</li>
              <li>Use services only for lawful business purposes</li>
              <li>Cooperate with security policies and procedures</li>
              <li>Pay all fees and charges in a timely manner</li>
              <li>Provide reasonable access to systems for service delivery</li>
            </ul>

            <h2 className="text-2xl font-bold font-heading text-slate-900 mt-8 mb-4">Prohibited Activities</h2>
            <p className="text-slate-600 mb-4">
              You may not use our services to:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Violate any laws, regulations, or third-party rights</li>
              <li>Transmit viruses, malware, or harmful code</li>
              <li>Attempt unauthorized access to systems or data</li>
              <li>Interfere with service operations or other users</li>
              <li>Engage in fraudulent or deceptive practices</li>
              <li>Distribute spam or unsolicited communications</li>
              <li>Use services to harm, threaten, or harass others</li>
              <li>Reverse engineer or copy our proprietary systems</li>
            </ul>

            <h2 className="text-2xl font-bold font-heading text-slate-900 mt-8 mb-4">Intellectual Property</h2>
            <p className="text-slate-600 mb-4">
              All content on the mimoIT website and in our services, including text, graphics, logos, software, and documentation, is the property of mimoIT or our licensors and is protected by copyright, trademark, and other intellectual property laws.
            </p>
            <p className="text-slate-600 mb-4">
              You may not reproduce, distribute, modify, or create derivative works without our express written permission. You are granted a limited, non-exclusive license to access and use our services for your business purposes only.
            </p>

            <h2 className="text-2xl font-bold font-heading text-slate-900 mt-8 mb-4">Payment Terms</h2>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li><strong>Fees:</strong> Service fees are specified in your service agreement</li>
              <li><strong>Payment Schedule:</strong> Invoices are due within 30 days unless otherwise specified</li>
              <li><strong>Late Payments:</strong> Late fees may apply to overdue balances</li>
              <li><strong>Taxes:</strong> Fees are exclusive of applicable taxes</li>
              <li><strong>Service Suspension:</strong> We may suspend services for non-payment after notice</li>
              <li><strong>Refunds:</strong> Fees are non-refundable except as specified in your service agreement</li>
            </ul>

            <h2 className="text-2xl font-bold font-heading text-slate-900 mt-8 mb-4">Service Availability</h2>
            <p className="text-slate-600 mb-4">
              We strive to maintain high service availability but do not guarantee uninterrupted access. Services may be temporarily unavailable due to:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Scheduled maintenance (with advance notice when possible)</li>
              <li>Emergency repairs or security updates</li>
              <li>Force majeure events beyond our control</li>
              <li>Third-party service provider outages</li>
              <li>Network or infrastructure failures</li>
            </ul>

            <h2 className="text-2xl font-bold font-heading text-slate-900 mt-8 mb-4">Data and Privacy</h2>
            <p className="text-slate-600 mb-4">
              Your use of our services is subject to our <Link to="/privacy" className="text-mimo-primary hover:text-mimo-accent font-semibold">Privacy Policy</Link>. We implement industry-standard security measures to protect your data, but you are responsible for maintaining backups of your critical data.
            </p>

            <h2 className="text-2xl font-bold font-heading text-slate-900 mt-8 mb-4">Limitation of Liability</h2>
            <p className="text-slate-600 mb-4">
              To the maximum extent permitted by law:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>mimoIT shall not be liable for indirect, incidental, special, or consequential damages</li>
              <li>Our total liability shall not exceed the fees paid by you in the 12 months preceding the claim</li>
              <li>We are not liable for third-party products, services, or failures</li>
              <li>Limitations apply except where prohibited by law</li>
            </ul>

            <h2 className="text-2xl font-bold font-heading text-slate-900 mt-8 mb-4">Warranties and Disclaimers</h2>
            <p className="text-slate-600 mb-4">
              We provide services on an "as is" and "as available" basis. We disclaim all warranties, express or implied, including warranties of merchantability, fitness for a particular purpose, and non-infringement, except as specifically stated in your service agreement.
            </p>

            <h2 className="text-2xl font-bold font-heading text-slate-900 mt-8 mb-4">Indemnification</h2>
            <p className="text-slate-600 mb-4">
              You agree to indemnify and hold harmless mimoIT, its officers, employees, and agents from any claims, damages, or expenses arising from:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Your use of our services</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any laws or regulations</li>
              <li>Your infringement of third-party rights</li>
            </ul>

            <h2 className="text-2xl font-bold font-heading text-slate-900 mt-8 mb-4">Term and Termination</h2>
            <p className="text-slate-600 mb-4">
              These Terms remain in effect while you use our website or services. Either party may terminate a service agreement according to its terms. Upon termination:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>You must cease using our services</li>
              <li>Outstanding fees become immediately due</li>
              <li>We will return or destroy your data as specified in your agreement</li>
              <li>Provisions that by nature should survive will continue in effect</li>
            </ul>

            <h2 className="text-2xl font-bold font-heading text-slate-900 mt-8 mb-4">Governing Law and Disputes</h2>
            <p className="text-slate-600 mb-4">
              These Terms are governed by the laws of the State of New Jersey, without regard to conflict of law principles. Any disputes shall be resolved through:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Good faith negotiations between the parties</li>
              <li>Mediation before a mutually agreed mediator</li>
              <li>Binding arbitration in New Jersey if mediation fails</li>
              <li>Courts in New Jersey for equitable relief when necessary</li>
            </ul>

            <h2 className="text-2xl font-bold font-heading text-slate-900 mt-8 mb-4">Changes to Terms</h2>
            <p className="text-slate-600 mb-4">
              We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting on this page with an updated "Last Updated" date. Material changes will be communicated via email. Your continued use of our services after changes constitutes acceptance of the modified Terms.
            </p>

            <h2 className="text-2xl font-bold font-heading text-slate-900 mt-8 mb-4">Severability</h2>
            <p className="text-slate-600 mb-4">
              If any provision of these Terms is found to be unenforceable, the remaining provisions will continue in full force and effect.
            </p>

            <h2 className="text-2xl font-bold font-heading text-slate-900 mt-8 mb-4">Entire Agreement</h2>
            <p className="text-slate-600 mb-4">
              These Terms, together with your service agreement and our Privacy Policy, constitute the entire agreement between you and mimoIT regarding your use of our website and services.
            </p>

            <h2 className="text-2xl font-bold font-heading text-slate-900 mt-8 mb-4">Contact Information</h2>
            <p className="text-slate-600 mb-4">
              Questions about these Terms should be directed to:
            </p>
            <div className="bg-slate-50 rounded-xl p-6 mb-8">
              <p className="text-slate-900 font-semibold mb-2">mimoIT Technology Solutions</p>
              <p className="text-slate-600">Email: <a href="mailto:info@mimoit.com" className="text-mimo-primary hover:text-mimo-accent">info@mimoit.com</a></p>
              <p className="text-slate-600">Phone: <a href="tel:201-473-4443" className="text-mimo-primary hover:text-mimo-accent">201-473-4443</a></p>
              <p className="text-slate-600">Web: <Link to="/contact" className="text-mimo-primary hover:text-mimo-accent">www.mimoit.com/contact</Link></p>
            </div>

            <div className="bg-mimo-primary/5 border-l-4 border-mimo-primary rounded p-6 mt-8">
              <p className="text-slate-700">
                <strong>By using our services, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.</strong>
              </p>
            </div>

          </div>

          {/* Related Pages */}
          <div className="mt-12 flex flex-wrap gap-4 justify-center">
            <Link to="/privacy" className="btn-outline inline-flex items-center gap-2">
              Privacy Policy
            </Link>
            <Link to="/sms-terms" className="btn-outline inline-flex items-center gap-2">
              SMS Terms
            </Link>
            <Link to="/" className="btn-primary inline-flex items-center gap-2">
              Return to Home
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsOfServicePage;
